/**
 * Fuzzy matching utilities for text similarity detection
 * Uses Levenshtein edit distance for comparing strings
 */

/**
 * Compute the Levenshtein edit distance between two strings (case-insensitive)
 *
 * Uses single-row dynamic programming for O(min(m,n)) space complexity.
 *
 * @param a - First string
 * @param b - Second string
 * @returns Edit distance (number of insertions, deletions, or substitutions)
 */
export function editDistance(a: string, b: string): number {
  const s = a.toLowerCase();
  const t = b.toLowerCase();

  if (s === t) return 0;
  if (s.length === 0) return t.length;
  if (t.length === 0) return s.length;

  // Ensure s is the shorter string for space optimization
  const short = s.length <= t.length ? s : t;
  const long = s.length <= t.length ? t : s;

  const row = Array.from({ length: short.length + 1 }, (_, i) => i);

  for (let i = 1; i <= long.length; i++) {
    let prev = row[0];
    row[0] = i;

    for (let j = 1; j <= short.length; j++) {
      const cost = long[i - 1] === short[j - 1] ? 0 : 1;
      const temp = row[j];
      row[j] = Math.min(
        row[j] + 1,      // deletion
        row[j - 1] + 1,  // insertion
        prev + cost       // substitution
      );
      prev = temp;
    }
  }

  return row[short.length];
}

/**
 * Find items similar to a query string based on edit distance
 *
 * Filters items where 0 < distance <= maxDistance, sorted by ascending distance.
 * Exact matches (distance === 0) are excluded since they are not "similar" but identical.
 *
 * @param query - The search query to compare against
 * @param items - Array of items to search through
 * @param getName - Function to extract the name string from each item
 * @param maxDistance - Maximum edit distance to consider a match (default: 3)
 * @returns Array of items sorted by similarity (closest first)
 */
export function findSimilarItems<T>(
  query: string,
  items: T[],
  getName: (item: T) => string,
  maxDistance: number = 3
): T[] {
  if (!query.trim()) return [];

  const scored = items
    .map((item) => ({
      item,
      distance: editDistance(query, getName(item)),
    }))
    .filter(({ distance }) => distance > 0 && distance <= maxDistance)
    .sort((a, b) => a.distance - b.distance);

  return scored.map(({ item }) => item);
}
